/**
 * Project Data Utilities
 * Helper functions for formatting project data in table displays
 */

/**
 * Extract agent nicknames from ProjectSalesAgent array
 * Falls back to salesForm.agent if salesAgents is empty
 * @param {Array} salesAgents - ProjectSalesAgent records with agent relation
 * @param {Object} salesForm - SalesForm with agent relation (fallback)
 * @returns {string} Comma-separated agent nicknames
 */
export function formatAgentNames(salesAgents, salesForm = null) {
  // Try to get names from salesAgents first
  if (salesAgents && salesAgents.length > 0) {
    const names = salesAgents
      .filter(sa => sa.agent && sa.isActive !== false)
      .map(sa => sa.agent.nickname || sa.agent.fullName)
      .filter(Boolean);

    if (names.length > 0) {
      return names.join(', ');
    }
  }

  // Fallback to salesForm.agent
  if (salesForm?.agent) {
    const agentName = salesForm.agent.nickname || salesForm.agent.fullName;
    if (agentName) {
      return agentName;
    }
  }

  return 'N/A';
}

/**
 * Extract leader nicknames from ProjectSalesAgent array
 * Falls back to salesForm.agent.leader if salesAgents is empty
 * @param {Array} salesAgents - ProjectSalesAgent records with salesLeader relation
 * @param {Object} salesForm - SalesForm with agent.leader relation (fallback)
 * @returns {string} Comma-separated leader nicknames (unique)
 */
export function formatLeaderNames(salesAgents, salesForm = null) {
  // Try to get names from salesAgents first
  if (salesAgents && salesAgents.length > 0) {
    const uniqueLeaders = new Map();
    salesAgents
      .filter(sa => sa.salesLeader && sa.isActive !== false)
      .forEach(sa => {
        const name = sa.salesLeader.nickname || sa.salesLeader.fullName;
        if (name) {
          uniqueLeaders.set(sa.salesLeader.id, name);
        }
      });

    if (uniqueLeaders.size > 0) {
      return Array.from(uniqueLeaders.values()).join(', ');
    }
  }

  // Fallback to salesForm.agent.leader
  if (salesForm?.agent?.leader) {
    const leaderName = salesForm.agent.leader.nickname || salesForm.agent.leader.fullName;
    if (leaderName) {
      return leaderName;
    }
  }

  return 'N/A';
}

/**
 * Extract manager nicknames from ProjectSalesAgent array
 * @param {Array} salesAgents - ProjectSalesAgent records with manager relation
 * @returns {string} Comma-separated manager nicknames (unique)
 */
export function formatManagerNames(salesAgents) {
  if (!salesAgents || salesAgents.length === 0) return 'N/A';

  const uniqueManagers = new Map();
  salesAgents
    .filter(sa => sa.manager && sa.isActive !== false)
    .forEach(sa => {
      const name = sa.manager.nickname || sa.manager.fullName;
      if (name) {
        uniqueManagers.set(sa.manager.id, name);
      }
    });

  if (uniqueManagers.size > 0) {
    return Array.from(uniqueManagers.values()).join(', ');
  }

  return 'N/A';
}

/**
 * Format full project location address
 * @param {Object} salesForm - SalesForm with address fields
 * @returns {string} Formatted address
 */
export function formatProjectLocation(salesForm) {
  if (!salesForm) return 'N/A';

  const parts = [
    salesForm.addressLine1,
    salesForm.addressLine2,
    salesForm.city,
    salesForm.state,
    salesForm.postcode
  ].filter(Boolean);

  if (parts.length === 0) {
    // Fallback to legacy propertyAddress field
    return salesForm.propertyAddress || 'N/A';
  }

  return parts.join(', ');
}

/**
 * Format SID assignment display
 * If ASID is handling, show "ASID Name (SID Name)", otherwise just SID name
 * @param {Array} teamMembers - ProjectTeamMember records with user and supervisorSid relations
 * @returns {string} Formatted SID/ASID display
 */
export function formatSidAssignment(teamMembers) {
  if (!teamMembers || teamMembers.length === 0) return 'Unassigned';

  // Find active SID or ASID team member
  const sidMember = teamMembers.find(
    tm => ['sid', 'asid'].includes(tm.roleType) && tm.isActive !== false
  );

  if (!sidMember || !sidMember.user) return 'Unassigned';

  const memberName = sidMember.user.nickname || sidMember.user.fullName;

  if (sidMember.roleType === 'asid') {
    // For ASID, show supervisor SID in parentheses
    // The SID info is in user.sid (from the User model's self-reference)
    const supervisorSid = sidMember.user.sid;
    if (supervisorSid) {
      const sidName = supervisorSid.nickname || supervisorSid.fullName;
      return `${memberName} (${sidName})`;
    }
  }

  return memberName || 'Unassigned';
}

/**
 * Format combined Sales Team display: "Agent(s) (Leader)"
 * Display rules:
 * - Single agent: "Ahmad (Siti)"
 * - Multiple agents, same leader: "Ahmad, Ali (Siti)"
 * - Multiple agents, different leaders: "Ahmad (Siti), Ali (Kumar)"
 * @param {Array} salesAgents - ProjectSalesAgent records with agent and salesLeader relations
 * @param {Object} salesForm - SalesForm with agent.leader relation (fallback)
 * @returns {string} Formatted sales team display
 */
export function formatSalesTeam(salesAgents, salesForm = null) {
  // Build agent-leader pairs from salesAgents
  const pairs = [];

  if (salesAgents && salesAgents.length > 0) {
    salesAgents
      .filter(sa => sa.agent && sa.isActive !== false)
      .forEach(sa => {
        const agentName = sa.agent.nickname || sa.agent.fullName;
        const leaderName = sa.salesLeader
          ? (sa.salesLeader.nickname || sa.salesLeader.fullName)
          : null;
        if (agentName) {
          pairs.push({ agent: agentName, leader: leaderName, leaderId: sa.salesLeader?.id });
        }
      });
  }

  // Fallback to salesForm if no pairs found
  if (pairs.length === 0 && salesForm?.agent) {
    const agentName = salesForm.agent.nickname || salesForm.agent.fullName;
    const leaderName = salesForm.agent.leader
      ? (salesForm.agent.leader.nickname || salesForm.agent.leader.fullName)
      : null;
    if (agentName) {
      pairs.push({ agent: agentName, leader: leaderName, leaderId: salesForm.agent.leader?.id });
    }
  }

  if (pairs.length === 0) return 'N/A';

  // Group agents by leader
  const leaderGroups = new Map();
  pairs.forEach(pair => {
    const key = pair.leaderId || 'no-leader';
    if (!leaderGroups.has(key)) {
      leaderGroups.set(key, { leader: pair.leader, agents: [] });
    }
    leaderGroups.get(key).agents.push(pair.agent);
  });

  // Format output
  const groupStrings = [];
  leaderGroups.forEach(group => {
    const agentStr = group.agents.join(', ');
    if (group.leader) {
      groupStrings.push(`${agentStr} (${group.leader})`);
    } else {
      groupStrings.push(agentStr);
    }
  });

  return groupStrings.join(', ');
}

/**
 * Format Sales Team as array of "Agent (Leader)" strings - one per agent
 * @param {Array} salesAgents - ProjectSalesAgent records with agent and salesLeader relations
 * @param {Object} salesForm - SalesForm with agent.leader relation (fallback)
 * @returns {Array<string>} Array of "Agent (Leader)" strings, one per agent
 */
export function formatSalesTeamLines(salesAgents, salesForm = null) {
  const lines = [];

  if (salesAgents && salesAgents.length > 0) {
    salesAgents
      .filter(sa => sa.agent && sa.isActive !== false)
      .forEach(sa => {
        const agentName = sa.agent.nickname || sa.agent.fullName;
        const leaderName = sa.salesLeader
          ? (sa.salesLeader.nickname || sa.salesLeader.fullName)
          : null;
        if (agentName) {
          lines.push(leaderName ? `${agentName} (${leaderName})` : agentName);
        }
      });
  }

  // Fallback to salesForm if no lines found
  if (lines.length === 0 && salesForm?.agent) {
    const agentName = salesForm.agent.nickname || salesForm.agent.fullName;
    const leaderName = salesForm.agent.leader
      ? (salesForm.agent.leader.nickname || salesForm.agent.leader.fullName)
      : null;
    if (agentName) {
      lines.push(leaderName ? `${agentName} (${leaderName})` : agentName);
    }
  }

  return lines;
}

export default {
  formatAgentNames,
  formatLeaderNames,
  formatManagerNames,
  formatProjectLocation,
  formatSidAssignment,
  formatSalesTeam,
  formatSalesTeamLines
};
